/**
 * Lophiel cinema hero — gentle slides + slow typewriter / fade text
 */
class LophielHero extends HTMLElement {
  connectedCallback() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.motionQuery.matches;
    this.handleMotionChange = (event) => {
      this.reducedMotion = event.matches;
      this.interval = this.reducedMotion ? 0 : this.requestedInterval;
      if (this.reducedMotion) {
        this.stopAutoplay();
        this.prepareSlideText(this.slides[this.index], true);
      } else {
        this.startAutoplay();
      }
      if (this.toggleEl) this.toggleEl.hidden = this.reducedMotion;
    };
    this.motionQuery.addEventListener('change', this.handleMotionChange);
    this.slides = [...this.querySelectorAll('.hero-cinema__slide, .hero-slide')];
    this.controls = [...this.querySelectorAll('.hero-slideshow__controls')];
    this.counterEl = this.querySelector('[data-hero-counter]');
    this.progressRingEl = this.querySelector('[data-hero-progress-ring]');
    this.toggleEl = this.querySelector('[data-hero-toggle]');
    this.pauseIconEl = this.querySelector('[data-hero-icon-pause]');
    this.playIconEl = this.querySelector('[data-hero-icon-play]');
    this.total = this.slides.length;
    this.pad = (n) => String(n).padStart(2, '0');
    this.isAnimating = false;
    this.animMs = 1500;
    this.typeTimer = null;
    this.controlsTimer = null;
    this.isPaused = false;
    this.ringLength = 113.097;

    if (this.slides.length < 1) return;

    this.index = 0;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === 0);
      if (i === 0) this.prepareSlideText(slide, true);
    });

    this.prepareControls(false);

    if (this.slides.length < 2) {
      this.updateUI(0);
      return;
    }

    this.requestedInterval = parseInt(this.dataset.autoplay, 10) || 0;
    this.interval = this.requestedInterval;
    if (this.reducedMotion) this.interval = 0;

    this.querySelectorAll('[data-hero-prev]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.go(-1);
      });
    });
    this.querySelectorAll('[data-hero-next]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.go(1);
      });
    });
    if (this.toggleEl) {
      this.toggleEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.togglePause();
      });
    }

    this.updateUI(0);
    this.updateToggleUI();
    if (this.interval > 0) this.startAutoplay();
    this.addEventListener('mouseenter', () => {
      if (!this.isPaused) this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      if (!this.isPaused) this.startAutoplay();
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.clearTypeTimer();
    this.clearControlsTimer();
    this.motionQuery?.removeEventListener('change', this.handleMotionChange);
  }

  go(delta) {
    if (this.isAnimating) return;
    const next = (this.index + delta + this.slides.length) % this.slides.length;
    this.show(next, delta);
  }

  show(index, delta = 1) {
    if (this.isAnimating || index === this.index) return;

    const prev = this.slides[this.index];
    const next = this.slides[index];
    if (!prev || !next) return;

    this.isAnimating = true;
    this.clearTypeTimer();
    const dir = typeof delta === 'number' && delta !== 0 ? (delta > 0 ? 1 : -1) : index > this.index ? 1 : -1;
    this.dataset.direction = String(dir);

    prev.classList.remove('is-active');
    prev.classList.add('is-exiting');
    this.resetSlideText(prev);

    next.classList.add('is-entering');
    next.offsetHeight;
    next.classList.add('is-active');
    this.prepareSlideText(next, false);

    window.setTimeout(() => {
      prev.classList.remove('is-exiting', 'is-entering');
      next.classList.remove('is-entering');
      this.isAnimating = false;
    }, this.animMs);

    this.index = index;
    this.updateUI(index);
    this.dispatchEvent(new CustomEvent('hero:change', { detail: { index } }));
    if (this.interval && !this.isPaused) this.restartProgress();
  }

  getHeadings(slide) {
    return [...slide.querySelectorAll('.hero-cinema__heading, .hero-slide__heading')];
  }

  getFadeTargets(slide) {
    return [
      ...slide.querySelectorAll('.hero-cinema__subheading, .hero-slide__subheading'),
      ...slide.querySelectorAll('.hero-cinema__description'),
      ...slide.querySelectorAll('.hero-cinema__actions, .hero-slide__cta'),
    ];
  }

  prepareControls(instant) {
    this.clearControlsTimer();

    this.controls.forEach((el) => {
      if (instant) {
        el.classList.add('is-hero-visible');
      } else {
        el.classList.remove('is-hero-visible');
      }
    });

    if (!instant && this.controls.length) {
      this.controlsTimer = window.setTimeout(() => {
        this.controls.forEach((el) => el.classList.add('is-hero-visible'));
        this.controlsTimer = null;
      }, 840);
    }
  }

  resetSlideText(slide) {
    this.getHeadings(slide).forEach((el) => {
      el.classList.remove('is-typing', 'is-typed');
      if (el.dataset.heroFullText) el.textContent = el.dataset.heroFullText;
    });
    this.getFadeTargets(slide).forEach((el) => el.classList.remove('is-hero-visible'));
  }

  prepareSlideText(slide, instant) {
    instant = instant || this.reducedMotion;
    this.prepareControls(instant);

    this.getFadeTargets(slide).forEach((el) => {
      if (instant) {
        el.classList.add('is-hero-visible');
      } else {
        el.classList.remove('is-hero-visible');
      }
    });

    this.getHeadings(slide).forEach((heading) => {
      if (!heading.dataset.heroFullText) {
        heading.dataset.heroFullText = heading.textContent.trim();
      }
      if (instant) {
        heading.textContent = heading.dataset.heroFullText;
        heading.classList.add('is-typed');
        return;
      }
      this.typewrite(heading);
    });

    if (!instant) {
      window.setTimeout(() => {
        this.getFadeTargets(slide).forEach((el) => el.classList.add('is-hero-visible'));
      }, 720);
    }
  }

  typewrite(el) {
    const full = el.dataset.heroFullText || '';
    if (this.reducedMotion) {
      el.textContent = full;
      el.classList.remove('is-typing');
      el.classList.add('is-typed');
      return;
    }
    el.textContent = '';
    el.classList.add('is-typing');
    el.classList.remove('is-typed');
    let i = 0;
    const step = () => {
      el.textContent = full.slice(0, i);
      i += 1;
      if (i <= full.length) {
        this.typeTimer = window.setTimeout(step, 58);
      } else {
        el.classList.remove('is-typing');
        el.classList.add('is-typed');
        this.typeTimer = null;
      }
    };
    step();
  }

  clearTypeTimer() {
    if (this.typeTimer) window.clearTimeout(this.typeTimer);
    this.typeTimer = null;
  }

  clearControlsTimer() {
    if (this.controlsTimer) window.clearTimeout(this.controlsTimer);
    this.controlsTimer = null;
  }

  updateUI(index) {
    if (this.counterEl) {
      this.counterEl.textContent = `${this.pad(index + 1)}`;
    }
  }

  restartProgress() {
    if (!this.interval) return;
    if (this.progressRingEl) {
      this.progressRingEl.style.transition = 'none';
      this.progressRingEl.style.strokeDashoffset = `${this.ringLength}`;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.progressRingEl) {
          this.progressRingEl.style.transition = `stroke-dashoffset ${this.interval}ms linear`;
          this.progressRingEl.style.strokeDashoffset = '0';
        }
      });
    });
  }

  startAutoplay() {
    if (!this.interval || this.isPaused || this.reducedMotion) return;
    this.stopAutoplay();
    this.restartProgress();
    this.timer = window.setInterval(() => this.go(1), this.interval);
  }

  stopAutoplay() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  updateToggleUI() {
    if (!this.toggleEl) return;
    const pauseLabel = this.toggleEl.dataset.labelPause || 'Pause slideshow';
    const playLabel = this.toggleEl.dataset.labelPlay || 'Play slideshow';
    const paused = this.isPaused || !this.interval;
    this.toggleEl.setAttribute('aria-pressed', paused ? 'true' : 'false');
    this.toggleEl.setAttribute('aria-label', paused ? playLabel : pauseLabel);
    if (this.pauseIconEl) this.pauseIconEl.classList.toggle('is-hidden', paused);
    if (this.playIconEl) this.playIconEl.classList.toggle('is-hidden', !paused);
    if (this.reducedMotion) this.toggleEl.hidden = true;
  }

  togglePause() {
    this.isPaused = !this.isPaused;
    if (this.isPaused) {
      this.stopAutoplay();
      if (this.progressRingEl) this.progressRingEl.style.transition = 'none';
    } else {
      this.startAutoplay();
    }
    this.updateToggleUI();
  }
}

if (!customElements.get('lophiel-hero')) {
  customElements.define('lophiel-hero', LophielHero);
}
